import { useEffect, useState } from "react";

import { Controller, useFormContext, FieldError, Merge, FieldErrorsImpl } from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";
import { useTypographyOptions } from "containers/admin/clients/client/theme-page/preview/hooks/use-typography-option";
import HexInput from "containers/admin/clients/client/theme-page/preview/components/hex-input/hex-input";
import ColorSwatch from "containers/admin/clients/client/theme-page/preview/components/color-swatch/color-swatch";
import { Select } from "ui/select";
import TextAlignment from "containers/admin/clients/client/theme-page/preview/sub-tabs/typography/components/text-alignment/text-alignment";

import {
  getAdobeFontWeights,
  getGoogleFontWeights,
} from "containers/admin/clients/client/theme-page/preview/helpers/formatters-on-post-data/formatters-on-post-data";

import { TypographyTabInputType } from "containers/admin/clients/client/theme-page/preview/preview-sidebar/preview-sidebar.types";
import {
  TypographyKeys,
  AppTypographyFormData,
  CustomSelectOption,
  AppFormatTypographyInputOptions,
  BasicSelectOption,
} from "containers/admin/clients/client/theme-page/preview/preview.types";

import { APP_WEIGHT_LABELS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/fonts.constants";

import Icons from "assets";

const TypographySectionContent = ({
  sectionName,
  sectionTitle,
  content,
}: {
  sectionName: TypographyKeys;
  sectionTitle: string;
  content: TypographyTabInputType[];
}) => {
  const [fontFamilyIsChanged, setFontFamilyIsChanged] = useState(false);

  // getting context
  const { isEditMode, setTypography, typography, colors, googleFontsList, adobeFontsList } =
    useThemePreviewContext();
  const [currentFontWeights, setCurrentFontWeights] = useState<BasicSelectOption[]>([]);

  const TYPOGRAPHY_OPTIONS = useTypographyOptions({ currentFontWeights });

  const isThemeSet = typography && colors;

  //form
  const {
    control,
    formState: { errors },
    watch,
    clearErrors,
    setError,
    getValues,
    setValue,
  } = useFormContext();

  const typographyErrors = errors?.typography as Record<
    string,
    FieldError | Merge<FieldError, FieldErrorsImpl<AppTypographyFormData>>
  >;
  const typographyErrorsSection = typographyErrors?.[sectionName] as Record<
    string,
    FieldError | Merge<FieldError, FieldErrorsImpl<AppFormatTypographyInputOptions>>
  >;

  const getTypographySelectValue = (name: keyof AppFormatTypographyInputOptions, field: any) => {
    if (!isThemeSet) return null;

    const isColorField = name === "textColor" || name === "linkColor";

    const shouldShowAlertIcon = (name: string) => {
      if (name === "textColor") {
        return !!typography[sectionName]?.textColor?.isAlertShown;
      }
      if (name === "linkColor") {
        return !!typography.paragraph?.linkColor?.isAlertShown;
      }
      return false;
    };

    const label = isColorField ? (
      <div className="flex gap-1 relative w-full">
        <ColorSwatch color={colors[field.value]} />
        {field.label}
        {shouldShowAlertIcon(name as string) && (
          <Icons.ExclamationCircle className="overflow-visible absolute text-critical-700 -bottom-0.5 size-6 left-[180px]" />
        )}
      </div>
    ) : (
      <div id="text" className="truncate max-w-[20ch]">
        <span className="text-ellipsis">{field.label}</span>
      </div>
    );

    return {
      value: field.value,
      label: label,
    };
  };

  const getUniqueFontWeights = () => {
    const fontFamily = getValues(`typography.${sectionName}.fontFamily`);

    //determine if the font is an adobe font or a google font
    const currentAdobeFont = adobeFontsList.find((font) => font.name === fontFamily.label);
    const currentGoogleFont = googleFontsList.find((font) => font.family === fontFamily.label)!;

    //get the weights for the chosen font
    const weights = currentAdobeFont
      ? getAdobeFontWeights(currentAdobeFont?.variations || [])
      : getGoogleFontWeights(currentGoogleFont?.variants);

    const uniqueWeights = Array.from(new Set(weights as string[]));

    return uniqueWeights;
  };

  // set errors for custom color on selecting custom and empty custom color
  const selectedColorName =
    watch(`typography.${sectionName}.textColor`)?.value === "custom" &&
    getValues(`typography.${sectionName}.customColor`) === "";

  const handleChange = (
    input: CustomSelectOption,
    field: string,
    fieldOnChange: (value: CustomSelectOption) => void,
  ) => {
    const { label, value, isAlertShown } = input;
    const inputVal =
      (field === "textColor" || field === "linkColor") && isAlertShown
        ? { label, value, isAlertShown }
        : { label, value };

    setTypography((prev: AppTypographyFormData | null) => {
      if (!prev) return prev;

      return {
        ...prev,
        [sectionName]: {
          ...prev[sectionName],
          [field]: inputVal,
          customColor: "",
        },
      };
    });

    if (field === "textColor" && value !== "custom") {
      setValue(`typography.${sectionName}.customColor`, "");
      clearErrors(`typography.${sectionName}.customColor`);
    }
    if (field === "linkColor" && value !== "custom") {
      setValue("typography.paragraph.customLinkColor", "");
      clearErrors("typography.paragraph.customLinkColor");
    }

    if (field === "fontFamily") {
      setFontFamilyIsChanged(true);
    }

    fieldOnChange({ label, value });
  };

  // Seperate function to handle changes to custom color because it is structured differently than other textColor options
  const handleCustomChange = (
    value: string,
    field: string,
    fieldOnChange: (value: string) => void,
  ) => {
    const hasError = typographyErrorsSection?.[field] ?? false;

    if (value !== "#" && !hasError && value !== "") {
      setTypography((prev: AppTypographyFormData | null) => {
        if (!prev) return prev;
        return {
          ...prev,
          [sectionName]: {
            ...prev[sectionName],
            [field]: value,
          },
        };
      });
    }
    fieldOnChange(value);
  };

  useEffect(() => {
    if (selectedColorName) {
      setError(`typography.${sectionName}.customColor`, {
        type: "custom",
        message: "Please provide 6 characters for a valid hex code",
      });
    }
  }, [selectedColorName, sectionName, setError]);

  //for paragraph link color only
  const paragraphLinkColorName =
    watch("typography.paragraph.linkColor")?.value === "custom" &&
    getValues("typography.paragraph.customLinkColor") === "";

  useEffect(() => {
    if (paragraphLinkColorName) {
      setError(`typography.paragraph.customLinkColor`, {
        type: "custom",
        message: "Please provide 6 characters for a valid hex code",
      });
    }
  }, [paragraphLinkColorName, sectionName, setError]);

  useEffect(() => {
    const uniqueWeights = getUniqueFontWeights();

    //every time font family is changed, update the font weight options
    const currentWeightOptions = uniqueWeights.map((weight) => ({
      label: APP_WEIGHT_LABELS[weight],
      value: String(weight),
    }));

    setCurrentFontWeights(currentWeightOptions);
    // eslint-disable-next-line
  }, [watch(`typography.${sectionName}.fontFamily`)]);

  useEffect(() => {
    if (fontFamilyIsChanged) {
      const uniqueWeights = getUniqueFontWeights();

      //determine default font weight when another font family is chosen
      const fontWeight = {
        label: APP_WEIGHT_LABELS[uniqueWeights[0]] || "Select...",
        value: String(uniqueWeights[0]),
      };

      //update typography with the default font weight
      const updatedTypography = { ...typography! };
      updatedTypography[sectionName].fontWeight = fontWeight;

      //reset font weight in typography
      setTypography(updatedTypography);

      //update the font weight in the form
      setValue(`typography.${sectionName}.fontWeight`, fontWeight);
    }
    // eslint-disable-next-line
  }, [fontFamilyIsChanged]);

  return (
    isThemeSet && (
      <div className="flex flex-col font-semibold gap-5 text-sm">
        {content.map(({ name, label, isRequired }: TypographyTabInputType) => {
          const inputName = `typography.${sectionName}.${name}`;

          if (name === "alignment") {
            return (
              <TextAlignment
                key={name}
                name={inputName}
                label="Alignment"
                sectionName={sectionName}
              />
            );
          }

          return (
            <Controller
              key={name}
              name={inputName}
              control={control}
              render={({ field }) => (
                <Select
                  label={label === "Color" ? `${sectionTitle} ${label}` : label}
                  ariaLabel={label}
                  selectName={`typography-select-${name}`}
                  disableClearable
                  options={TYPOGRAPHY_OPTIONS[name]}
                  required={isRequired}
                  disabled={!isEditMode}
                  {...field}
                  value={getTypographySelectValue(name, field.value)}
                  onChange={(el) => {
                    if (el) {
                      handleChange(el, name as string, field.onChange);
                    }
                  }}
                />
              )}
            />
          );
        })}

        {watch(`typography.${sectionName}.textColor`)?.value === "custom" && (
          <Controller
            name={`typography.${sectionName}.customColor`}
            control={control}
            render={({ field }) => (
              <div className="mt-2">
                <HexInput
                  label="Custom color"
                  isRequired={true}
                  isDisabled={!isEditMode}
                  handleCustomChange={(value, isColorPickerInvoked) => {
                    if (isColorPickerInvoked) clearErrors(`typography.${sectionName}.customColor`);

                    handleCustomChange(value, "customColor", field.onChange);
                  }}
                  errMessage={(typographyErrorsSection?.customColor as FieldError)?.message}
                  inputNameKey="custom"
                  {...field}
                />
              </div>
            )}
          />
        )}
        {sectionName === "paragraph" &&
          watch("typography.paragraph.linkColor")?.value === "custom" && (
            <Controller
              name="typography.paragraph.customLinkColor"
              control={control}
              render={({ field }) => (
                <div className="mt-2">
                  <HexInput
                    label="Custom link color"
                    isRequired={true}
                    isDisabled={!isEditMode}
                    handleCustomChange={(value, isColorPickerInvoked) => {
                      if (isColorPickerInvoked) clearErrors("typography.paragraph.customLinkColor");

                      handleCustomChange(value, "customLinkColor", field.onChange);
                    }}
                    errMessage={(typographyErrorsSection?.customLinkColor as FieldError)?.message}
                    inputNameKey="custom"
                    {...field}
                  />
                </div>
              )}
            />
          )}
      </div>
    )
  );
};

export default TypographySectionContent;
